import React, { useState, useEffect } from 'react';
import { colors } from '../../styles/designTokens';

// Helper function to get status colors
const getStatusColors = (status) => {
  const statusLower = status?.toLowerCase() || '';

  if (statusLower.includes('approv') || statusLower.includes('sanction') || statusLower.includes('payout')) {
    return {
      bg: colors.status.approved.bg,
      text: colors.status.approved.text,
      border: colors.status.approved.text,
    };
  } else if (statusLower.includes('reject') || statusLower.includes('cancel')) {
    return {
      bg: colors.status.rejected.bg,
      text: colors.status.rejected.text,
      border: colors.status.rejected.text,
    };
  } else if (statusLower.includes('disburs')) {
    return {
      bg: colors.status.disbursed.bg,
      text: colors.status.disbursed.text,
      border: colors.status.disbursed.text,
    };
  } else if (
    statusLower.includes('process') ||
    statusLower.includes('pending') ||
    statusLower.includes('verification') ||
    statusLower.includes('valuation') ||
    statusLower.includes('check') ||
    statusLower.includes('rto') ||
    statusLower.includes('collection') ||
    statusLower.includes('search') ||
    statusLower.includes('paper') ||
    statusLower.includes('hold') ||
    statusLower.includes('response') ||
    statusLower.includes('follow') ||
    statusLower.includes('lead')
  ) {
    return {
      bg: colors.status.pending.bg,
      text: colors.status.pending.text,
      border: colors.status.pending.text,
    };
  } else if (statusLower.includes('basic') || statusLower.includes('documents') || statusLower.includes('info') || statusLower.includes('details')) {
    return {
      bg: '#E0F2FE', // light blue
      text: '#0369A1', // dark blue
      border: '#7DD3FC', // medium blue
    };
  } else {
    return {
      bg: colors.neutral.gray100,
      text: colors.neutral.gray700,
      border: colors.neutral.gray400,
    };
  }
};

// Colored Status Dropdown Component with Manual Custom Status Support
export const StatusDropdown = ({ value, onChange, options = [], className = '', ...props }) => {
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');

  // Reset custom input text when mode toggles or value changes
  useEffect(() => {
    if (!isCustomMode) {
      setCustomText('');
    }
  }, [isCustomMode, value]);

  const handleSelectChange = (e) => {
    const selectedVal = e.target.value;
    if (selectedVal === '__MANUAL_CUSTOM__') {
      setIsCustomMode(true);
      setCustomText(value || '');
    } else {
      onChange(e);
    }
  };

  const handleCustomSave = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const trimmed = customText.trim();
    if (trimmed) {
      // Create synthetic event matching standard select onChange
      const syntheticEvent = {
        target: { value: trimmed },
        stopPropagation: () => {},
        preventDefault: () => {}
      };
      onChange(syntheticEvent);
    }
    setIsCustomMode(false);
  };

  const handleCustomCancel = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsCustomMode(false);
    setCustomText('');
  };

  // Check if current value exists in predefined options list
  const existingValues = new Set(options.map(o => o.value));
  const hasCurrentValueInOptions = !value || existingValues.has(value);

  const statusColors = getStatusColors(value);

  if (isCustomMode) {
    return (
      <div 
        className={`flex items-center gap-1.5 ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCustomSave(e);
            if (e.key === 'Escape') handleCustomCancel(e);
          }}
          placeholder="Type custom status..."
          autoFocus
          className="px-2 py-1 text-xs sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full bg-white text-gray-800 border-blue-400 font-semibold shadow-inner min-w-[130px]"
        />
        <button
          type="button"
          onClick={handleCustomSave}
          title="Save Status"
          className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-md transition-colors shadow flex-shrink-0"
        >
          ✓
        </button>
        <button
          type="button"
          onClick={handleCustomCancel}
          title="Cancel"
          className="px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white font-bold text-xs rounded-md transition-colors shadow flex-shrink-0"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <select
      value={value || ''}
      onChange={handleSelectChange}
      className={`px-2.5 py-1.5 rounded-lg border text-xs sm:text-sm font-semibold focus:ring-2 focus:outline-none cursor-pointer transition-colors duration-150 shadow-sm w-full min-w-[145px] max-w-[210px] truncate ${className}`}
      style={{
        backgroundColor: statusColors.bg,
        color: statusColors.text,
        borderColor: statusColors.border,
        borderWidth: '1px',
      }}
      {...props}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}

      {/* Render current custom status as selected option if not in predefined list */}
      {!hasCurrentValueInOptions && (
        <option value={value}>
          {value}
        </option>
      )}

      {/* Manual custom status option */}
      <option value="__MANUAL_CUSTOM__">
        ✏️ Type Custom Status...
      </option>
    </select>
  );
};

